import React, { useState, useMemo } from 'react';
import AppLayout from '../components/AppLayout';
import Button from '../components/ui/Button';
import SearchBar from '../components/database/SearchBar';
import StatusFilters from '../components/database/StatusFilters';
import JobsTableHeader from '../components/database/JobsTableHeader';
import JobsTable from '../components/database/JobsTable';
import JobsCardView from '../components/database/JobsCardView';
import Pagination from '../components/database/Pagination';
import ExportButton from '../components/database/ExportButton';
import JobDetailView from '../components/jobs/JobDetailView';
import NewJobSheet from '../components/jobs/NewJobSheet';
import AssignJobSheet from '../components/jobs/AssignJobSheet';
import { useJobs } from '../hooks/useJobs';
import { useToast } from '../hooks/useToast';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { List, LayoutGrid } from 'lucide-react';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export default function JobsDatabasePage() {
  const { jobs, loading, updateJob, deleteJob } = useJobs();
  const toast = useToast();

  // State
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showRepairsOnly, setShowRepairsOnly] = useState(false);
  const [sortBy, setSortBy] = useState('soldDate');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('cards'); // 'list' or 'cards'
  const [showJobDetail, setShowJobDetail] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [showAssignJobSheet, setShowAssignJobSheet] = useState(false);

  const ITEMS_PER_PAGE = 25;

  // Filter and sort jobs
  const filteredJobs = useMemo(() => {
    let result = [...jobs];

    // Search filter - includes contractNumber
    if (searchText.trim()) {
      const query = searchText.toLowerCase();
      result = result.filter(job => {
        const fields = [
          job.customerName,
          job.contractNumber,
          job.name,
          job.notes,
          job.jobType,
          job.address || '',
          job.status || ''
        ];
        return fields.some(field => 
          field && field.toString().toLowerCase().includes(query)
        );
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(job => job.status === statusFilter);
    }

    // Repairs only filter
    if (showRepairsOnly) {
      result = result.filter(job => job.isRepair);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'soldDate':
          return new Date(a.soldDate) - new Date(b.soldDate);
        case 'scheduledDate':
          // Jobs on hold go to end (treat as no date)
          const aDate = a.status === 'on_hold' ? new Date('9999-12-31') : (a.scheduledDate || new Date('9999-12-31'));
          const bDate = b.status === 'on_hold' ? new Date('9999-12-31') : (b.scheduledDate || new Date('9999-12-31'));
          return aDate - bDate;
        case 'customerName':
          return (a.customerName || '').localeCompare(b.customerName || '');
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        default:
          return 0;
      }
    });

    return result;
  }, [jobs, searchText, statusFilter, showRepairsOnly, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredJobs.length / ITEMS_PER_PAGE);
  const paginatedJobs = filteredJobs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchText, statusFilter, showRepairsOnly, sortBy]);

  function handleJobClick(job) {
    setSelectedJob(job);
    setShowJobDetail(true);
  }

  function handleEditJob(job) {
    setSelectedJob(job);
    setShowJobDetail(false);
    setShowEditSheet(true);
  }

  async function handleScheduleJob(job) {
    setSelectedJob(job);
    setShowJobDetail(false);
    setShowAssignJobSheet(true);
  }

  async function handleCompleteJob(job) {
    if (!confirm(`Mark "${job.customerName || job.name}" as completed?`)) return;
    
    try {
      await updateJob(job.id, { status: 'completed' });
      setShowJobDetail(false);
      toast.success('Job marked as complete');
    } catch (error) {
      console.error('❌ Error completing job:', error);
      toast.error('Failed to complete job');
    }
  }

  async function handleCancelJob(job) {
    if (!confirm(`Cancel job for "${job.customerName || job.name}"?`)) return;
    
    try {
      await updateJob(job.id, { status: 'cancelled' });
      setShowJobDetail(false);
      toast.success('Job cancelled');
    } catch (error) {
      console.error('❌ Error cancelling job:', error);
      toast.error('Failed to cancel job');
    }
  }

  async function handleDeleteJob(job) {
    if (!confirm(`Permanently delete job for "${job.customerName || job.name}"? This CANNOT be undone!`)) return;
    
    try {
      await deleteJob(job.id);
      setShowJobDetail(false);
      toast.success('Job deleted');
    } catch (error) {
      console.error('❌ Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  }

  async function handleStatusChange(job, newStatus, onHoldReason = null) {
    try {
      const updates = { status: newStatus };
      
      // If setting to "On Hold", include reason
      if (newStatus === 'on_hold' && onHoldReason) {
        updates.onHoldReason = onHoldReason;
      }
      
      await updateJob(job.id, updates);
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('❌ Error updating status:', error);
      toast.error('Failed to update status');
    }
  }

  const breadcrumb = 'Dashboard › Jobs Database';

  if (loading) {
    return (
      <AppLayout title="Jobs Database" breadcrumb={breadcrumb}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px'
        }}>
          <LoadingSpinner size="large" color={WALNUT} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Jobs Database"
      breadcrumb={breadcrumb}
      topBarActions={
        <>
          <Button size="small">+ Add Job</Button>
          <ExportButton jobs={filteredJobs} />
        </>
      }
    >
      <div style={{ fontFamily: FONT_SANS }}>
        {/* Header */}
        <div style={{ background: PAPER, borderRadius: '16px', padding: '20px 24px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{
                fontFamily: FONT_DISPLAY,
                fontSize: '24px',
                fontWeight: '600',
                color: INK,
                margin: '0 0 4px 0'
              }}>
                Production Overview
              </h2>
              <p style={{
                fontSize: '13px',
                color: STONE,
                margin: 0
              }}>
                Real-time status of all jobs — from new sale to installation
              </p>
            </div>

            {/* View Toggle */}
            <div style={{
              display: 'flex',
              gap: '4px',
              background: 'rgba(139,69,19,0.06)',
              padding: '4px',
              borderRadius: '10px'
            }}>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px',
                  background: viewMode === 'list' ? PAPER : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  fontFamily: FONT_SANS,
                  color: viewMode === 'list' ? WALNUT : STONE,
                  cursor: 'pointer',
                  boxShadow: viewMode === 'list' ? '0 2px 4px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                <List size={14} strokeWidth={2} /> List
              </button>
              <button
                onClick={() => setViewMode('cards')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px',
                  background: viewMode === 'cards' ? PAPER : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  fontFamily: FONT_SANS,
                  color: viewMode === 'cards' ? WALNUT : STONE,
                  cursor: 'pointer',
                  boxShadow: viewMode === 'cards' ? '0 2px 4px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                <LayoutGrid size={14} strokeWidth={2} /> Cards
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '24px' }}>
          <SearchBar
            value={searchText}
            onChange={setSearchText}
            placeholder="Search by customer, contract #, location, status..."
          />
        </div>

        {/* Filters */}
        <div style={{ background: PAPER, borderRadius: '16px', padding: '20px 24px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', marginBottom: '24px' }}>
          <StatusFilters
            selected={statusFilter}
            onChange={setStatusFilter}
            showRepairsOnly={showRepairsOnly}
            onRepairsToggle={setShowRepairsOnly}
          />
        </div>

        {/* Results Summary */}
        <div style={{ background: PAPER, borderRadius: '16px', padding: '20px 24px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: STONE,
                marginBottom: '4px'
              }}>
                {searchText || statusFilter !== 'all' || showRepairsOnly ? 'Filtered Results' : 'All Jobs'}
              </div>
              <div style={{
                fontFamily: FONT_DISPLAY,
                fontSize: '28px',
                fontWeight: '600',
                color: INK
              }}>
                {filteredJobs.length}
              </div>
            </div>

            {(searchText || statusFilter !== 'all' || showRepairsOnly) && (
              <button
                onClick={() => {
                  setSearchText('');
                  setStatusFilter('all');
                  setShowRepairsOnly(false);
                }}
                style={{
                  padding: '8px 16px',
                  background: 'transparent',
                  border: `1px solid ${STONE}55`,
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  fontFamily: FONT_SANS,
                  color: STONE,
                  cursor: 'pointer'
                }}
              >
                Clear All Filters
              </button>
            )}
          </div>
        </div>

        {/* Sort Header */}
        <JobsTableHeader sortBy={sortBy} onSortChange={setSortBy} />

        {/* Jobs List/Cards */}
        <div style={{ background: PAPER, borderRadius: '16px', padding: '20px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', marginBottom: '24px' }}>
          {viewMode === 'list' ? (
            <JobsTable
              jobs={paginatedJobs}
              onJobClick={handleJobClick}
            />
          ) : (
            <JobsCardView
              jobs={paginatedJobs}
              onJobClick={handleJobClick}
            />
          )}
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={filteredJobs.length}
          itemsPerPage={ITEMS_PER_PAGE}
        />
      </div>

      {/* Job Detail Modal */}
      {selectedJob && (
        <JobDetailView
          job={selectedJob}
          isOpen={showJobDetail}
          onClose={() => setShowJobDetail(false)}
          onEdit={handleEditJob}
          onSchedule={handleScheduleJob}
          onComplete={handleCompleteJob}
          onCancel={handleCancelJob}
          onDelete={handleDeleteJob}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Edit Job Sheet */}
      <NewJobSheet
        isOpen={showEditSheet}
        onClose={() => setShowEditSheet(false)}
        editingJob={selectedJob}
      />

      {/* Assign Job Sheet */}
      <AssignJobSheet
        isOpen={showAssignJobSheet}
        onClose={() => setShowAssignJobSheet(false)}
        presetJob={selectedJob}
      />
    </AppLayout>
  );
}